'use client';

import React, { useState, useEffect } from 'react';
import { Shield, Eye, Database, Users, Lock, Globe, AlertTriangle, FileText, Cookie } from 'lucide-react';
import Footer from '@/components/Footer';

export default function PrivacyPage() {
  const [effectiveDate, setEffectiveDate] = useState<string>('');

  // Set date only on client side to avoid hydration mismatch
  useEffect(() => {
    setEffectiveDate(new Date().toLocaleDateString('en-US', { year: 'numeric', month: 'long', day: 'numeric' }));
  }, []);

  return (
    <div className="min-h-screen bg-gray-50">
      <div className="max-w-4xl mx-auto px-4 py-12 sm:px-6 lg:px-8">
        {/* Header */}
        <div className="text-center mb-12">
          <div className="flex items-center justify-center mb-4">
            <Shield className="h-8 w-8 text-blue-600 mr-3" />
            <h1 className="text-4xl font-bold text-gray-900">Privacy Policy</h1>
          </div>
          <p className="text-lg text-gray-600 max-w-2xl mx-auto">
            Effective Date: {effectiveDate || 'Loading...'}
          </p>
        </div>

        {/* Content */}
        <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-8 space-y-8">
          
          {/* Introduction */}
          <section>
            <h2 className="text-2xl font-semibold text-gray-900 mb-4 flex items-center">
              <Globe className="h-6 w-6 text-blue-600 mr-2" />
              1. Introduction
            </h2>
            <div className="prose prose-gray max-w-none">
              <p>
                SmartMetrics ("we," "our," or "us") respects your privacy and is committed to protecting your personal data. 
                This Privacy Policy comprehensively explains how we collect, use, store, and protect your information when you 
                visit our website, create an account, use our agency management platform, make payments, and utilize our services.
              </p>
            </div>
          </section>

          {/* Information We Collect */}
          <section>
            <h2 className="text-2xl font-semibold text-gray-900 mb-4 flex items-center">
              <Database className="h-6 w-6 text-blue-600 mr-2" />
              2. Information We Collect
            </h2>
            <div className="prose prose-gray max-w-none">
              <ul className="list-disc pl-6 space-y-3">
                <li>
                  <strong>Account Information:</strong> When you create an account, we collect your email address, name, 
                  password (encrypted), and optionally your company name. If you sign in with Google OAuth, we collect 
                  your Google account email and name.
                </li>
                <li>
                  <strong>Payment Information:</strong> When you make a payment, we collect billing information including 
                  name, email address, company name, and payment method details. Payment card information is processed 
                  securely by Stripe and is not stored on our servers. We store payment transaction records and subscription 
                  information for billing and accounting purposes.
                </li>
                <li>
                  <strong>Client and Project Data:</strong> We collect and store information about your clients, proposals, 
                  contracts, projects, and business relationships that you create or upload through our platform.
                </li>
                <li>
                  <strong>Usage Data:</strong> Information automatically collected, such as IP address, browser type, 
                  pages visited, duration of visit, interaction details, device information, and access logs.
                </li>
                <li>
                  <strong>Communication Data:</strong> We collect information from your communications with us, including 
                  support requests, feedback, and any information you provide when contacting us.
                </li>
                <li>
                  <strong>Cookies and Tracking:</strong> We use cookies, server-side tracking, and similar technologies 
                  to maintain your session, enhance your browsing experience, analyze website performance, and measure 
                  the effectiveness of our services.
                </li>
              </ul>
            </div>
          </section>

          {/* How We Use Your Information */}
          <section>
            <h2 className="text-2xl font-semibold text-gray-900 mb-4 flex items-center">
              <Eye className="h-6 w-6 text-blue-600 mr-2" />
              3. How We Use Your Information
            </h2>
            <div className="prose prose-gray max-w-none">
              <p>We use your information to:</p>
              <ul className="list-disc pl-6 space-y-2">
                <li>Provide, maintain, and improve our agency management platform and services.</li>
                <li>Process payments, manage subscriptions, and handle billing.</li>
                <li>Create and manage your account, authenticate your identity, and secure your account.</li>
                <li>Store and manage your client data, proposals, contracts, and project information.</li>
                <li>Enable client portals and facilitate communication between you and your clients.</li>
                <li>Send you important account-related notifications, service updates, and billing information.</li>
                <li>Respond to your inquiries and provide customer support.</li>
                <li>Send marketing communications and newsletters (you may opt out anytime).</li>
                <li>Detect, prevent, and address technical issues, fraud, or security threats.</li>
                <li>Comply with legal obligations and enforce our terms of service.</li>
                <li>Conduct analysis to enhance our offerings and identify trends.</li>
              </ul>
            </div>
          </section>

          {/* Sharing Your Information */}
          <section>
            <h2 className="text-2xl font-semibold text-gray-900 mb-4 flex items-center">
              <Users className="h-6 w-6 text-blue-600 mr-2" />
              4. Sharing Your Information
            </h2>
            <div className="prose prose-gray max-w-none">
              <p>We may share your information with third parties under the following circumstances:</p>
              <ul className="list-disc pl-6 space-y-2">
                <li>
                  <strong>Payment Processors:</strong> We share payment information with Stripe to process payments, 
                  manage subscriptions, and handle billing. Stripe's use of your information is governed by their 
                  privacy policy.
                </li>
                <li>
                  <strong>Service Providers:</strong> We may share information with trusted service providers who 
                  assist us in operating our platform, such as hosting providers, email services, and analytics 
                  services. These providers are contractually obligated to protect your information.
                </li>
                <li>
                  <strong>Legal Requirements:</strong> We may disclose information if required by law, court order, 
                  or government regulation, or to protect our rights, property, or safety, or that of our users.
                </li>
                <li>
                  <strong>Business Transfers:</strong> In connection with business transactions (mergers, acquisitions, 
                  or asset sales), we may transfer your information to the acquiring entity, with proper notice and 
                  confidentiality protections.
                </li>
                <li>
                  <strong>With Your Consent:</strong> We may share information with third parties when you explicitly 
                  consent to such sharing.
                </li>
              </ul>
              <p className="mt-4">
                <strong>We do not sell your personal information to third parties for their marketing purposes.</strong>
              </p>
            </div>
          </section>

          {/* Data Security */}
          <section>
            <h2 className="text-2xl font-semibold text-gray-900 mb-4 flex items-center">
              <Lock className="h-6 w-6 text-blue-600 mr-2" />
              5. Data Security
            </h2>
            <div className="prose prose-gray max-w-none">
              <p>
                We implement strict technical and organizational measures to protect your personal data against unauthorized 
                access, disclosure, alteration, or destruction. These measures include:
              </p>
              <ul className="list-disc pl-6 space-y-2 mt-2">
                <li>Encryption of data in transit using SSL/TLS protocols</li>
                <li>Encryption of sensitive data at rest</li>
                <li>Secure password hashing (bcrypt) for account credentials</li>
                <li>Access controls and authentication mechanisms</li>
                <li>Regular security audits and vulnerability assessments</li>
                <li>Secure payment processing through Stripe (we do not store payment card information)</li>
                <li>Limited access to personal data on a need-to-know basis</li>
              </ul>
              <p className="mt-4">
                However, no internet-based transmission or electronic storage is entirely secure. While we strive to protect 
                your information, we cannot guarantee absolute security. You are responsible for maintaining the security of 
                your account credentials.
              </p>
            </div>
          </section>

          {/* Data Retention and Deletion */}
          <section>
            <h2 className="text-2xl font-semibold text-gray-900 mb-4 flex items-center">
              <FileText className="h-6 w-6 text-blue-600 mr-2" />
              6. Data Retention and Deletion
            </h2>
            <div className="prose prose-gray max-w-none">
              <p>
                We retain your personal information for as long as necessary to provide our services and fulfill the purposes 
                described in this Privacy Policy, unless a longer retention period is required or permitted by law. Specifically:
              </p>
              <ul className="list-disc pl-6 space-y-2 mt-2">
                <li><strong>Account Information:</strong> Retained while your account is active and for a reasonable period 
                after account closure to comply with legal obligations and resolve disputes.</li>
                <li><strong>Payment Records:</strong> Retained for accounting and tax purposes as required by law (typically 
                7 years).</li>
                <li><strong>Client and Project Data:</strong> Retained while your account is active. You may delete this data 
                at any time through the platform.</li>
                <li><strong>Usage Data:</strong> Retained for analytics and service improvement purposes, typically for up to 
                2 years.</li>
              </ul>
              <p className="mt-4">
                Upon account deletion, we will delete or anonymize your personal information within 30-90 days, except where 
                we are required to retain it for legal, accounting, or security purposes. You may request deletion of your 
                account and data by contacting support@smartmetrics.com.
              </p>
            </div>
          </section>

          {/* Your Data Protection Rights */}
          <section>
            <h2 className="text-2xl font-semibold text-gray-900 mb-4 flex items-center">
              <Shield className="h-6 w-6 text-green-600 mr-2" />
              7. Your Data Protection Rights
            </h2>
            <div className="prose prose-gray max-w-none">
              <p>You have rights, including:</p>
              <ul className="list-disc pl-6 space-y-2">
                <li>Access and rectify your personal information.</li>
                <li>Request deletion of your data.</li>
                <li>Restrict or object to data processing.</li>
                <li>Withdraw consent at any time.</li>
              </ul>
              <p className="mt-4">
                Please contact us via the information provided below to exercise these rights.
              </p>
            </div>
          </section>

          {/* Cookies */}
          <section>
            <h2 className="text-2xl font-semibold text-gray-900 mb-4 flex items-center">
              <Cookie className="h-6 w-6 text-blue-600 mr-2" />
              8. Cookies
            </h2>
            <div className="prose prose-gray max-w-none">
              <p>
                We use cookies and similar technologies to operate the site, remember your preferences, and measure
                performance. You can manage your choices at any time through the cookie banner or the
                &quot;Do Not Sell or Share My Personal Information&quot; link in the footer.
              </p>
              <p>
                The table below lists the cookies we use and why we use them.
              </p>
            </div>
            <div className="cky-audit-table-element mt-6" />
          </section>

          {/* Third-Party Links */}
          <section>
            <h2 className="text-2xl font-semibold text-gray-900 mb-4 flex items-center">
              <Globe className="h-6 w-6 text-blue-600 mr-2" />
              9. Third-Party Links
            </h2>
            <div className="prose prose-gray max-w-none">
              <p>
                Our website may include links to third-party websites. We are not responsible for their privacy practices; 
                please review their policies separately.
              </p>
            </div>
          </section>

          {/* Updates to This Policy */}
          <section>
            <h2 className="text-2xl font-semibold text-gray-900 mb-4 flex items-center">
              <AlertTriangle className="h-6 w-6 text-orange-600 mr-2" />
              10. Updates to This Policy
            </h2>
            <div className="prose prose-gray max-w-none">
              <p>
                We reserve the right to update this Privacy Policy periodically. We will prominently notify you of 
                significant changes via email or by posting the updated policy on our website homepage.
              </p>
            </div>
          </section>

          {/* Contact Information */}
          <section>
            <h2 className="text-2xl font-semibold text-gray-900 mb-4">Contact Information</h2>
            <div className="prose prose-gray max-w-none">
              <p>
                For questions or concerns regarding this Privacy Policy, please contact us:
              </p>
              <ul className="list-none pl-0 space-y-1">
                <li><strong>Email:</strong> support@smartmetrics.com</li>
              </ul>
              <p className="mt-6 font-medium text-gray-900">
                Thank you for trusting SmartMetrics.
              </p>
            </div>
          </section>

        </div>
      </div>
      <Footer />
    </div>
  );
}
